import {useEffect, useRef, useState} from "react";
import useFetchClient from "./useFetchClient";
import {
  ObservabilityMetricSet,
  ObservabilityMetricState,
  ObservabilityPeriod,
  ObservabilityTimeSeriesResponse
} from "../types/observability";

const emptyMetricState: ObservabilityMetricState = {
  series: null,
  total: 0,
  from: null,
  to: null,
  loading: true,
  error: null
}

const periodDurationMs: Record<ObservabilityPeriod, number> = {
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000
}

const getPeriodRange = (period: ObservabilityPeriod) => {
  const now = new Date()
  const from = new Date(now.getTime() - periodDurationMs[period])

  return {
    from,
    to: now
  }
}

const toLocalIsoString = (date: Date) => {
  const offsetMinutes = -date.getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absoluteOffsetMinutes = Math.abs(offsetMinutes)
  const offsetHours = Math.floor(absoluteOffsetMinutes / 60).toString().padStart(2, '0')
  const offsetRemainingMinutes = (absoluteOffsetMinutes % 60).toString().padStart(2, '0')

  const year = date.getFullYear().toString()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hour = date.getHours().toString().padStart(2, '0')
  const minute = date.getMinutes().toString().padStart(2, '0')
  const second = date.getSeconds().toString().padStart(2, '0')

  return `${year}-${month}-${day}T${hour}:${minute}:${second}${sign}${offsetHours}:${offsetRemainingMinutes}`
}

const findMetricSet = (metricSets: ObservabilityMetricSet[], metricSetId: string) => {
  return metricSets.find(metricSet => metricSet.metricSetId === metricSetId) ?? null
}

const toMetricState = (
  metricSet: ObservabilityMetricSet | null,
  from: string,
  to: string
): ObservabilityMetricState => {
  const series = metricSet?.series?.[0] ?? null

  return {
    series,
    total: series?.total ?? 0,
    from,
    to,
    loading: false,
    error: null
  }
}

const useObservabilityTimeSeries = (
  endpoint: string,
  metricSetIds: string[],
  period: ObservabilityPeriod
) => {
  const fetchClient = useFetchClient()
  const fetchClientRef = useRef(fetchClient)
  const [metrics, setMetrics] = useState<Record<string, ObservabilityMetricState>>({})
  fetchClientRef.current = fetchClient

  useEffect(() => {
    let cancelled = false
    const range = getPeriodRange(period)
    const from = toLocalIsoString(range.from)
    const to = toLocalIsoString(range.to)

    setMetrics(currentMetrics => {
      return metricSetIds.reduce<Record<string, ObservabilityMetricState>>((nextMetrics, metricSetId) => {
        nextMetrics[metricSetId] = {
          ...(currentMetrics[metricSetId] ?? emptyMetricState),
          loading: true,
          error: null
        }
        return nextMetrics
      }, {...currentMetrics})
    })

    fetchClientRef.current.post(endpoint, {
      from,
      to,
      metricSetIds
    })
      .then(response => {
        if (cancelled) {
          return
        }

        const metricResponse = response.data as ObservabilityTimeSeriesResponse
        const metricSets = metricResponse.metricSets ?? []
        const responseFrom = metricResponse.from ?? from
        const responseTo = metricResponse.to ?? to

        setMetrics(currentMetrics => {
          return metricSetIds.reduce<Record<string, ObservabilityMetricState>>((nextMetrics, metricSetId) => {
            nextMetrics[metricSetId] = toMetricState(
              findMetricSet(metricSets, metricSetId),
              responseFrom,
              responseTo
            )
            return nextMetrics
          }, {...currentMetrics})
        })
      })
      .catch(error => {
        if (cancelled) {
          return
        }

        console.log('useObservabilityTimeSeries error:', error)
        setMetrics(currentMetrics => {
          return metricSetIds.reduce<Record<string, ObservabilityMetricState>>((nextMetrics, metricSetId) => {
            nextMetrics[metricSetId] = {
              ...(currentMetrics[metricSetId] ?? emptyMetricState),
              loading: false,
              error
            }
            return nextMetrics
          }, {...currentMetrics})
        })
      })

    return () => {
      cancelled = true
    }
  }, [endpoint, metricSetIds, period])

  return {
    metrics
  }
}

export default useObservabilityTimeSeries
